import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, Mail, Phone, MessageSquare, Clock, HelpCircle, 
  ChevronDown, ChevronUp, Send, ShieldAlert, LifeBuoy, FileText, X
} from 'lucide-react';
import Sidebar2 from '../../Components/SideBar2/SideBar2';
import Header2 from '../../Components/Header2/Header2';
import styles from './Support.module.css';

// BACKEND ARCHITECTURE CONFIGURATIONS
const WEBSOCKET_URL = "wss://api.brookessoftware.com/live-chat";
const HTTP_API_URL = "https://api.brookessoftware.com/api";

export default function Support() {
  const memberName = localStorage.getItem("memberName") || "Member";
  const memberNumber = localStorage.getItem("memberNumber") || "N/A";

  // UI States
  const [openFaq, setOpenFaq] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  
  // Real-time Data Sync Architecture States
  const [messages, setMessages] = useState([]);
  
  // Ref hooks for tracking WebSocket instances and Auto-Scrolling elements
  const socketRef = useRef(null);
  const chatBottomRef = useRef(null);

  const [formData, setFormData] = useState({
    category: 'general',
    subject: '',
    message: ''
  });

  // Automatically scroll viewport layout down to view new arrivals
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isChatOpen]);

  // Connect to live updates when the user activates the system tray drawer
  useEffect(() => {
    if (!isChatOpen) {
      if (socketRef.current) {
        socketRef.current.close();
      }
      return;
    }

    // 1. Ingest historical threads from HTTP persistence layer
    fetch(`${HTTP_API_URL}/chat/history?memberNumber=${memberNumber}`)
      .then(res => res.json())
      .then(data => setMessages(data.history || []))
      .catch(err => console.error("Could not recover database logs:", err));

    // 2. Initialize duplex continuous WebSocket channel instance
    const ws = new WebSocket(`${WEBSOCKET_URL}?senderId=${memberNumber}&role=client`);
    socketRef.current = ws;

    ws.onmessage = (event) => {
      const incomingData = JSON.parse(event.data);
      // Expected structure payload schema context: { senderId, text, timestamp, isAdmin }
      setMessages((prev) => [...prev, incomingData]);
    };

    ws.onclose = () => console.log("Real-time session connection channel detached.");
    ws.onerror = (error) => console.error("WS Pipe Exception:", error);

    return () => {
      if (ws) ws.close();
    };
  }, [isChatOpen, memberNumber]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Support ticket submitted successfully!\nCategory: ${formData.category}`);
    setFormData({ category: 'general', subject: '', message: '' });
  };

  // Dispatch data bundle through downstream streaming sockets
  const handleSendLiveMessage = (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || !socketRef.current) return;

    const outgoingPayload = {
      senderId: memberNumber,
      senderName: memberName,
      text: chatMessage.trim(),
      timestamp: new Date().toISOString(),
      isAdmin: false
    };

    // Broadcast instantly to active gateway pipelines
    socketRef.current.send(JSON.stringify(outgoingPayload));

    // Optimistically update standard client localized viewport structures
    setMessages((prev) => [...prev, outgoingPayload]);
    setChatMessage("");
  };

  const faqs = [
    {
      question: "How long does it take for a loan application to be approved?",
      answer: "Standard loan applications are typically reviewed and approved within 24 to 48 business hours, provided all required documentation has been uploaded correctly."
    },
    {
      question: "How can I update my monthly automated savings amount?",
      answer: "You can modify your monthly savings goal directly via the 'Quick Actions' widget on your main dashboard or by creating a request through this support portal under 'Savings Management'."
    },
    {
      question: "Are there any hidden charges for processing manual wallet withdrawals?",
      answer: "No. We pride ourselves on operational transparency. Standard withdrawal transactions carry zero hidden fees, though normal regulatory third-party bank charges may apply."
    }
  ];

  return (
    <div className={styles.container}>
      <Sidebar2 />

      <main className={styles.mainContent}>
        <Header2 />

        <div className={styles.workspace}>
          
          {/* WELCOME BANNER PANEL */}
          <div className={styles.welcomeContainer}>
            <div className={styles.welcomeBanner}>
              <div className={styles.welcomeContent}>
                <span className={styles.wavingHand}><LifeBuoy size={32} strokeWidth={1.5} /></span>
                <div className={styles.textStack}>
                  <h3 className={styles.welcomeText}>Support Center</h3>
                  <h2 className={styles.memberName}>How can we assist you today?</h2>
                  <div className={styles.membershipBadge}>
                    <p className={styles.membershipNo}>Logged in as: {memberName} ({memberNumber})</p>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.welcomeBanner2}>
              <div className={styles.walletInfo}>
                <h3 className={styles.walletNumber}>Support Availability</h3>
                <div className={styles.walletValueRow}>
                  <Clock className={styles.walletIcon} size={22} />
                  <h2 className={styles.walletValue}>24 / 7</h2>
                </div>
                <p className={styles.walletBalanceLabel}>Our digital desk is always live for queues.</p>
              </div>
              <div className={styles.fundContainer}>
                <span className={styles.walletBalanceLabel} style={{ marginBottom: 5 }}>Emergency hotlines</span>
                <a href="tel:+2348161241827" className={styles.fundButton} style={{ textDecoration: 'none', textAlign: 'center' }}>
                  Call Hotline
                </a>
              </div>
            </div>
          </div>

          {/* DIRECT CONTACT CHANNELS - 3 COLUMN GRID */}
          <div className={styles.threeColGrid}>
            <div className={styles.card}>
              <div className={`${styles.iconWrapper} ${styles.greenIcon}`}><Mail size={20} /></div>
              <h3 className={styles.cardTitle} style={{ marginTop: '1rem' }}>Email Support</h3>
              <p className={styles.channelDesc}>Drop us an email. Our team typically replies within 2 hours.</p>
              <a href="mailto:hello@brookessoftware.com" className={styles.channelLink}>hello@brookessoftware.com</a>
            </div>

            <div className={styles.card}>
              <div className={`${styles.iconWrapper} ${styles.blueIcon}`}><Phone size={20} /></div>
              <h3 className={styles.cardTitle} style={{ marginTop: '1rem' }}>Telephone Lines</h3>
              <p className={styles.channelDesc}>Speak directly with our cooperative service representatives.</p>
              <a href="tel:+2348161241827" className={styles.channelLink}>+234 816 124 1827</a>
            </div>

            {/* LIVE CHAT INTERACTION TRIGGER COMPONENT */}
            <div className={styles.card}>
              <div className={`${styles.iconWrapper} ${styles.purpleIcon}`}><MessageSquare size={20} /></div>
              <h3 className={styles.cardTitle} style={{ marginTop: '1rem' }}>Live Chat Desk</h3>
              <p className={styles.channelDesc}>Instant secure conversations with live support experts.</p>
              <button 
                onClick={() => setIsChatOpen(true)}
                className={styles.greenLinkButton} 
                style={{ padding: 0, marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <span>Start Session</span> <ArrowRight size={12} />
              </button>
            </div>
          </div>

          {/* TWO COLUMN CONTENT - SYSTEM TICKETING FORM & FAQ SYSTEM */}
          <div className={styles.twoColGrid}>
            <div className={styles.card}>
              <div className={styles.loanHeader}>
                <FileText size={18} style={{ color: '#15803d' }} />
                <h3 className={styles.loanTitle}>Open a Support Ticket</h3>
              </div>
              
              <form onSubmit={handleSubmit} className={styles.ticketForm}>
                <div className={styles.formGroup}>
                  <label htmlFor="category" className={styles.formLabel}>Inquiry Category</label>
                  <select 
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className={styles.selectDropdown}
                    style={{ width: '100%', margin: 0 }}
                  >
                    <option value="general">General Inquiries</option>
                    <option value="savings">Savings & Welfare Plans</option>
                    <option value="loans">Loan Schemes & Repayments</option>
                    <option value="wallet">Wallet Funding & Top-ups</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="subject" className={styles.formLabel}>Subject Title</label>
                  <input 
                    type="text" 
                    id="subject"
                    name="subject"
                    placeholder="Provide a descriptive summary heading..."
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message" className={styles.formLabel}>Elaborated Message Body</label>
                  <textarea 
                    id="message"
                    name="message"
                    rows="4"
                    placeholder="Detail your request or challenge comprehensively here..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    required
                  ></textarea>
                </div>

                <button type="submit" className={styles.submitFormBtn}>
                  <span>Dispatch Ticket</span> <Send size={14} />
                </button>
              </form>
            </div>

            <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              <div className={styles.loanHeader} style={{ marginBottom: '1.5rem' }}>
                <HelpCircle size={18} style={{ color: '#15803d' }} />
                <h3 className={styles.loanTitle}>Frequently Asked Questions</h3>
              </div>

              <div className={styles.faqWrapper}>
                {faqs.map((faq, index) => (
                  <div key={index} className={styles.faqItem}>
                    <button 
                      type="button" 
                      className={styles.faqHeader}
                      onClick={() => toggleFaq(index)}
                    >
                      <span className={styles.faqQuestion}>{faq.question}</span>
                      {openFaq === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <div className={`${styles.faqBody} ${openFaq === index ? styles.faqBodyActive : ''}`}>
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.securityNotice}>
                <ShieldAlert size={20} className={styles.noticeIcon} />
                <div>
                  <h4 className={styles.noticeTitle}>Security Guardrail Statement</h4>
                  <p className={styles.noticeDesc}>Cooperative agents will never ask you to reveal passwords, login tokens, or transactional PIN configurations via support requests.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* MODERN FIXED IN-APP LIVE CHAT DRAW OVERLAY POPUP */}
      {isChatOpen && (
        <div className={styles.chatWidgetContainer}>
          <div className={styles.chatWindow}>
            
            {/* Header Area */}
            <div className={styles.chatHeader}>
              <div className={styles.adminProfile}>
                <div className={styles.statusDot} />
                <div>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>Live Support Desk</h4>
                  <span style={{ fontSize: '11px', opacity: 0.9 }}>Typically replies instantly</span>
                </div>
              </div>
              <button onClick={() => setIsChatOpen(false)} className={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>

            {/* Message Stream Body */}
            <div className={styles.chatBody}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', marginTop: '40%', fontSize: '14px' }}>
                  <MessageSquare size={32} style={{ color: '#cbd5e1', marginBottom: 8 }} />
                  <p style={{ margin: 0 }}>Send a message to initialize a real-time thread session.</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div 
                    key={index} 
                    className={`${styles.messageRow} ${msg.isAdmin ? styles.adminRow : styles.userRow}`}
                  >
                    <div className={`${styles.bubble} ${msg.isAdmin ? styles.adminBubble : styles.userBubble}`}>
                      <p style={{ margin: 0 }}>{msg.text}</p>
                      <span className={styles.timestamp}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatBottomRef} />
            </div>

            {/* Message Bar Entry Tray Form */}
            <form onSubmit={handleSendLiveMessage} className={styles.chatInputForm}>
              <input 
                type="text" 
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Type your message here..."
                className={styles.chatInput}
              />
              <button type="submit" disabled={!chatMessage.trim()} className={styles.sendMsgBtn}>
                <Send size={16} style={{ marginLeft: '-2px' }} />
              </button>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}